import React, { useEffect, useReducer, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
// @ts-ignore — legacy JS modules without types
import Layout from "../components/Layout";
// @ts-ignore
import { init as initRaw, patientReducer as patientReducerRaw } from "../additional_files/reducer";
// @ts-ignore
import { verifyUser } from "../authRequests";
import { AccountLayout } from "../components/Account/AccountLayout";
import { BillingView } from "../components/Account/Billing/BillingView";
import { PlansView } from "../components/Account/Plans/PlansView";
import { usePlans } from "../components/Account/Plans/hooks";
import { MethodsView } from "../components/Account/Methods/MethodsView";
import { CreditsView } from "../components/Account/Credits/CreditsView";
import { useSessionBootstrap } from "../components/Account/Billing/hooks";

type DashAction = { type: string; payload?: any; [key: string]: any };
const patientReducer = patientReducerRaw as React.Reducer<any, DashAction>;
const init = initRaw as (state: any) => any;

const TAB_KEYS = ["billing", "memberships", "credits", "methods"] as const;
type TabKey = (typeof TAB_KEYS)[number];

function isTabKey(value: string | undefined | null): value is TabKey {
  return !!value && (TAB_KEYS as readonly string[]).includes(value);
}

export default function Account() {
  const { state } = useLocation() as { state: any };
  const params = useParams();
  const [dashState, dispatch] = useReducer(patientReducer, state, init);
  const loadedRef = useRef<HTMLDivElement | null>(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    verifyUser(state ? state.jwt : "", loadedRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsHydration = !dashState?.patient?.pid;
  const bootstrap = useSessionBootstrap({ jwt: null }, needsHydration);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (!bootstrap.data) return;
    hydratedRef.current = true;
    // The legacy patientReducer assumes every collection field is an array
    // (e.g. `state.appointments.concat(action.payload)` on NEW_APPOINTMENT
    // from the socket). `loadDataForPatientLogin` populated all of these in
    // the legacy login flow; bootstrap only returns what the Account page
    // needs, so we have to defaults-fill the rest here so the reducer never
    // sees `undefined` on a socket-driven action.
    dispatch({
      type: "UPDATE_STATE",
      payload: {
        ...dashState,
        db: bootstrap.data.db,
        patient: bootstrap.data.patient,
        organization: bootstrap.data.organization,
        locations: bootstrap.data.locations,
        selectedLocation: bootstrap.data.selectedLocation,
        firstLoading: false,
        userType: bootstrap.data.userType,
        guardianOf: bootstrap.data.dependents || [],
        appointments: dashState?.appointments || [],
        doctors: dashState?.doctors || [],
        notifications: dashState?.notifications || [],
        services: dashState?.services || [],
        serviceCriteria: dashState?.serviceCriteria || [],
        insurers: dashState?.insurers || [],
        relationship: dashState?.relationship || null,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrap.data]);

  const tab: TabKey = isTabKey(params.tab) ? params.tab : "billing";

  const locationId: string | null =
    dashState?.selectedLocation ||
    dashState?.patient?.homeLocationId ||
    (Array.isArray(dashState?.patient?.lid)
      ? dashState.patient.lid[0]
      : dashState?.patient?.lid) ||
    null;
  const auth = { jwt: dashState?.jwt || null };
  // A clinic that sells no plans shows no Memberships tab to a patient who
  // holds none (memberships audit C48).
  const plans = usePlans(auth);
  const hiddenTabs: readonly TabKey[] =
    plans.data && !plans.data.offered && plans.data.plans.length === 0 ? ["memberships"] : [];
  const organizationId: string | null =
    dashState?.organization?.oid ||
    dashState?.organization?.organizationId ||
    null;

  return (
    <Layout state={{ ...dashState }} dispatch={dispatch}>
      <div
        ref={loadedRef}
        style={{ opacity: 0 }}
        className="relative flex flex-1 w-full h-full flex-col"
      >
        <AccountLayout active={tab} hidden={hiddenTabs}>
          {tab === "billing" && (
            <BillingView auth={auth} locationId={locationId} />
          )}
          {tab === "memberships" && <PlansView auth={auth} />}
          {tab === "credits" && (
            <CreditsView
              auth={auth}
              locationId={locationId}
              organizationId={organizationId}
            />
          )}
          {tab === "methods" && (
            <MethodsView auth={auth} locationId={locationId} />
          )}
        </AccountLayout>
      </div>
    </Layout>
  );
}
